<template>
    <div>
        <LoadingSpinner v-if="loader" />

        <!-- No Seller -->
        <div v-else-if="user === null" class="no-user-state">
            <i class="mdi mdi-account-off-outline no-user-icon"></i>
            <h5 class="no-user-title">{{ $t('sentence.noUserFound') }}</h5>
            <button class="btn btn-outline-primary btn-sm" @click="$router.back()">
                {{ $t('action.back') }}
            </button>
        </div>

        <div v-else class="seller-info-modern mb-4">
            <!-- Main Profile Section -->
            <div class="profile-header">
                <div class="profile-main">
                    <div @click="moveToUserPage(user.id)" class="avatar-wrapper cursor-pointer">
                        <div class="avatar-container">
                            <img v-if="user?.picture" :src="config.storageUrl + user?.picture" alt="Profile"
                                class="avatar-image" />
                            <div v-else class="avatar-placeholder">
                                {{ user?.username?.[0]?.toUpperCase() }}
                            </div>
                            <div v-if="user?.rc" class="verified-badge">
                                <i class="mdi mdi-check-decagram"></i>
                            </div>
                        </div>
                    </div>

                    <div class="profile-info">
                        <div class="name-section">
                            <h2 @click="moveToUserPage(user.id)" class="username cursor-pointer mb-2">
                                {{ user?.username }}
                            </h2>
                            <div class="user-meta">
                                <span class="location-badge" v-if="user?.city">
                                    <i class="mdi mdi-map-marker"></i>
                                    {{ user.city }}
                                    <span v-if="user?.commune">/ {{ user.commune.name }}</span>
                                </span>
                            </div>
                        </div>

                        <div class="stats-row">
                            <div class="stat-item">
                                <i class="mdi mdi-star stat-icon"></i>
                                <span class="stat-value">{{ user?.average_rating !== 0 ? user?.average_rating.toFixed(1) : 'N/D' }}</span>
                                <span class="stat-label">({{ user?.ratings?.length ?? 0 }} avis)</span>
                            </div>
                            <div class="stat-divider"></div>
                            <div class="stat-item">
                                <i class="mdi mdi-package-variant stat-icon"></i>
                                <span class="stat-value">{{ productsCount }}</span>
                                <span class="stat-label">{{ $t("annonce") }}</span>
                            </div>
                        </div>

                        <div class="activity-info">
                            <div class="activity-item">
                                <i class="mdi mdi-calendar-clock"></i>
                                <span>{{ $t("Membre depuis") }} {{ formatDate(user?.created_at) }}</span>
                            </div>
                            <div class="activity-item">
                                <i class="mdi mdi-clock-outline"></i>
                                <span>{{ $t("Dernière activité") }} {{ user?.last_activity | moment }}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Action Buttons -->
                <div class="btn-user d-flex flex-wrap gap-2 mt-3">
                    <button v-if="authUser && user.id !== authUser.id" class="btn btn-follow"
                        :class="{ 'btn-following': isSellerFavorited }" @click="toggleSellerFavorite"
                        :disabled="loadingFavorite">
                        <i class="mdi mr-1" :class="isSellerFavorited ? 'mdi-heart' : 'mdi-heart-outline'"></i>
                        <span>{{ isSellerFavorited ? $t("action.following") : $t("action.follow") }}</span>
                    </button>

                    <button v-if="showMessageButton" @click="messageAboutProduct" class="btn btn-message">
                        <i class="mdi mdi-message-text mr-1"></i>
                        <span>{{ $t("action.message") }}</span>
                    </button>
                </div>
            </div>

            <!-- Badges Section -->
            <div class="badges-section">
                <div class="badge-card responsive">
                    <i class="mdi mdi-lightning-bolt badge-icon"></i>
                    <span>Réactif</span>
                </div>
                <div class="badge-card" :class="user?.rc ? 'verified' : 'unverified'">
                    <i class="mdi" :class="user?.rc ? 'mdi-shield-check badge-icon' : 'mdi-shield-alert badge-icon'"></i>
                    <span>{{ user?.rc ? 'Vérifié' : 'Non vérifié' }}</span>
                </div>
                <div class="badge-card seller" v-if="user?.is_seller">
                    <i class="mdi mdi-store badge-icon"></i>
                    <span>Vendeur professionnel</span>
                </div>
            </div>
        </div>

        <notifications class="custom-notifications mt-2" group="success" position="top center" />
        <notifications class="custom-notifications mt-2" group="error" position="top center" />
    </div>
</template>

<script>
import LoadingSpinner from '@/components/templates/LoadingSpinner.vue';
import helper from "@/services/helper";
import config from "@/config";

export default {
    name: "ProfileSection",
    components: { LoadingSpinner },
    props: {
        user_id: { type: [Number, String], default: null },
        product_id: { type: [Number, String], default: null },
        showMessageButton: { type: Boolean, default: false }
    },
    data() {
        return {
            user: null,
            productsCount: 0,
            config,
            loader: false,
            isSellerFavorited: false,
            loadingFavorite: false,
        };
    },
    watch: {
        user_id: {
            immediate: true,
            handler(val) {
                if (!val) return;
                this.getUserProfile();
            }
        }
    },
    mounted() {
        this.getUserProfile();
    },
    computed: {
        authUser() {
            return this.$store.state.user;
        }
    },
    methods: {
        formatDate: helper.formatDate,
        moveToUserPage(id) {
            if (this.$route.name !== "Profile") {
                this.$router.push({ name: "Profile", params: { id } });
            }
        },
        getUserProfile() {
            if (!this.user_id && !this.$route.params.id) return;

            const id = this.$route.name !== "Profile" ? this.user_id : this.$route.params.id;
            this.loader = true;

            this.$axios.get(`user/profile/${id}`)
                .then(({ data }) => {
                    if (data?.data) {
                        this.user = data.data;
                        this.productsCount = (data.data.products || []).filter(p => p.is_active).length;
                        this.checkSellerFavoriteStatus();
                        this.$emit('user-loaded', data.data);
                    } else {
                        this.user = null;
                        this.productsCount = 0;
                    }
                })
                .catch(err => {
                    console.error("Error fetching user profile:", err);
                    this.user = null;
                })
                .finally(() => {
                    this.loader = false;
                });
        },
        messageAboutProduct() {
            const data = {
                product_id: this.product_id,
                quantity: 1,
                text: '*',
                type: 'ABOUT',
            };

            this.loader = true;
            this.$axios({
                url: "offers/create-about",
                method: "POST",
                data,
            })
                .then((res) => {
                    this.loader = false;
                    if (res.data && res.data.success) {
                        this.$router.push({
                            name: "messenger",
                            params: { id: this.user_id, productId: this.product_id },
                        });
                    } else {
                        this.$notify({
                            group: "error",
                            title: "Erreur",
                            text: (res.data && res.data.message) || "Une erreur s'est produite.",
                            type: "error",
                        });
                    }
                })
                .catch((err) => {
                    this.loader = false;
                    if (err.response && err.response.status === 403) {
                        this.$router.push("login-user");
                    } else {
                        this.$notify({
                            group: "error",
                            title: "Erreur",
                            text: (err.response && this.$t(err.response.data.message)) || "Une erreur s'est produite.",
                            type: "error",
                        });
                    }
                });
        },
        async checkSellerFavoriteStatus() {
            if (!this.user?.id || !this.authUser?.id) return;

            try {
                const response = await this.$axios.get(`/favorite-sellers/check/${this.user.id}`);
                this.isSellerFavorited = response.data.data.favorited;
            } catch (error) {
                console.error('Error checking seller favorite status:', error);
            }
        },
        async toggleSellerFavorite() {
            if (!this.user?.id) return;
            if (!this.authUser?.id) {
                this.$router.push({ name: "login" });
                return;
            }

            this.loadingFavorite = true;
            try {
                const response = await this.$axios.post(`/favorite-sellers/toggle/${this.user.id}`);
                this.isSellerFavorited = response.data.status;
                this.getUserProfile();
            } catch (error) {
                console.error('Error toggling seller favorite:', error);
                if (error.response && error.response.status === 403) {
                    this.$router.push('login-user');
                } else {
                    this.$notify({
                        group: 'error',
                        title: 'Erreur',
                        text: error.response?.data?.message || 'Une erreur s\'est produite.',
                        type: 'error',
                    });
                }
            } finally {
                this.loadingFavorite = false;
            }
        }
    }
};
</script>

<style scoped>
/* Profile Styles */
.seller-info-modern {
    background: linear-gradient(135deg, white 0%, var(--gray-50) 100%);
    overflow: hidden;
    border: 1px solid var(--gray-200);
}

.profile-header {
    padding: 2rem;
    background: white;
}

.profile-main {
    display: flex;
    gap: 1.5rem;
    align-items: flex-start;
}

.avatar-wrapper {
    flex-shrink: 0;
}

.avatar-container {
    position: relative;
    width: 100px;
    height: 100px;
}

.avatar-image,
.avatar-placeholder {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
    border: 4px solid white;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.avatar-placeholder {
    background: linear-gradient(135deg, var(--indigo) 0%, var(--purple) 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2.5rem;
    font-weight: 700;
    color: white;
}

.verified-badge {
    position: absolute;
    bottom: 0;
    right: 0;
    background: linear-gradient(135deg, var(--cyan) 0%, var(--color-nz) 100%);
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 3px solid white;
    box-shadow: 0 2px 8px rgba(38, 149, 255, 0.3);
}

.verified-badge i {
    color: white;
    font-size: 18px;
}

.profile-info {
    flex: 1;
    min-width: 0;
}

.name-section {
    margin-bottom: 1rem;
}

.username {
    font-size: 1.75rem;
    font-weight: 700;
    color: var(--color-dark);
    margin: 0;
    line-height: 1.2;
    transition: color 0.2s;
}

.username:hover {
    color: var(--color-nz);
}

.user-meta {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
}

.location-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.25rem 0.75rem;
    background: var(--gray-50);
    border-radius: 20px;
    font-size: 0.875rem;
    color: var(--gray-600);
    border: 1px solid var(--gray-200);
}

.stats-row {
    display: flex;
    align-items: center;
    gap: 1.5rem;
    margin-bottom: 1rem;
    flex-wrap: wrap;
}

.stat-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.stat-icon {
    color: var(--color-id-400);
    font-size: 1.25rem;
}

.stat-value {
    font-size: 1.125rem;
    font-weight: 700;
    color: var(--color-dark);
}

.stat-label {
    font-size: 0.875rem;
    color: var(--gray-500);
}

.stat-divider {
    width: 1px;
    height: 24px;
    background: var(--gray-200);
}

.activity-info {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.activity-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    color: var(--gray-500);
}

.badges-section {
    display: flex;
    gap: 0.75rem;
    padding: 1.5rem 2rem;
    background: var(--gray-50);
    border-top: 1px solid var(--gray-200);
    flex-wrap: wrap;
}

.badge-card {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.625rem 1rem;
    border-radius: 10px;
    font-size: 0.875rem;
    font-weight: 600;
    transition: transform 0.2s;
}

.badge-card:hover {
    transform: translateY(-2px);
}

.badge-card.responsive {
    background: linear-gradient(135deg, var(--success-200) 0%, var(--success-300) 100%);
    color: white;
    box-shadow: 0 2px 8px rgba(76, 175, 80, 0.3);
}

.badge-card.verified {
    background: linear-gradient(135deg, var(--color-nz) 0%, var(--color-nz-200) 100%);
    color: white;
    box-shadow: 0 2px 8px rgba(38, 149, 255, 0.3);
}

.badge-card.unverified {
    background: var(--danger-light);
    color: var(--color-oo-500);
    border: 1px solid var(--color-oo-200);
}

.badge-card.seller {
    background: linear-gradient(135deg, var(--warning) 0%, var(--warning-200) 100%);
    color: white;
    box-shadow: 0 2px 8px rgba(245, 158, 11, 0.3);
}

.no-user-state {
    padding: 32px;
    border: 1px dashed var(--gray-300);
    border-radius: 12px;
    text-align: center;
    background-color: var(--gray-50);
}

.no-user-icon {
    font-size: 48px;
    color: var(--gray-400);
    margin-bottom: 12px;
}

.no-user-title {
    font-weight: 600;
    margin-bottom: 16px;
    color: var(--gray-800);
}

.cursor-pointer {
    cursor: pointer;
}

.gap-2 {
    gap: 0.5rem;
}

@media (max-width: 768px) {
    .profile-header {
        padding: 1.5rem;
    }

    .profile-main {
        flex-direction: column;
        align-items: center;
        text-align: center;
    }

    .avatar-container {
        width: 90px;
        height: 90px;
    }

    .username {
        font-size: 1.5rem;
    }

    .stats-row {
        justify-content: center;
    }

    .btn-user {
        justify-content: center;
        width: 100%;
    }

    .badges-section {
        padding: 1rem 1.5rem;
        justify-content: center;
    }

    .activity-info {
        align-items: center;
    }

    .seller-info-modern {
        padding-bottom: 50px;
    }
}
</style>